import { reconstituteCurrency } from '@helpers/currency';
import { getStackTrace, log } from '@src/Log.init';
import { convertCurrencyToDinero } from 'features/session/sessionSlice';
import { rootApi } from './root.api';

export const sessionApi = rootApi.injectEndpoints({
  tagTypes: ['Session'],
  endpoints: (builder) => ({
    getSession: builder.query({
      query: ({ token }) => ({
        url: 'context',
        method: 'GET',
        headers: { token },
      }),
      providesTags: ['Session'],
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({ message_type: 'Session', message: 'getSession: pending' });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Session',
            message: 'getSession: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getSession: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
    }),
    searchTransactions: builder.query({
      query: ({ criteria }) => {
        const queryString = Object.keys(criteria)
          .map((key) => `${key}=${criteria[key]}`)
          .join('&');
        return {
          url: `transactions?${queryString}`,
          method: 'GET',
        };
      },
      providesTags: ['Session'],
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({
          message_type: 'Session',
          message: 'searchTransactions: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Session',
            message: 'searchTransactions: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'searchTransactions: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
      transformResponse(response) {
        log.debug({
          message_type: 'Session',
          message: 'searchTransactions: transformResponse',
          data: response,
        });
        const res = response?.[0]?.data.map((transaction) => {
          transaction.total_gross = transaction.total_gross
            ? reconstituteCurrency(transaction.total_gross)
            : null;
          transaction.total_net = transaction.total_net
            ? reconstituteCurrency(transaction.total_net)
            : null;
          transaction.total_tax = transaction.total_tax
            ? reconstituteCurrency(transaction.total_tax)
            : null;
          return transaction;
        });

        return { ...response[0], data: res };
      },
    }),
    getReceipt: builder.query({
      query: ({ sessionId }) => ({
        url: `receipt/${sessionId}`,
        method: 'GET',
      }),
      providesTags: ['Session'],
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({ message_type: 'Session', message: 'getReceipt: pending' });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Session',
            message: 'getReceipt: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getReceipt: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
      async transformResponse(response) {
        log.debug({
          message_type: 'Session',
          message: 'getReceipt: transformResponse',
          data: response,
        });
        return convertCurrencyToDinero(response);
      },
    }),
    requestImpersonationSession: builder.mutation({
      query: ({ tenantId, clientId }) => ({
        url: 'impersonate',
        method: 'POST',
        timeout: 5000,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: { tenant_id: tenantId, client_id: clientId },
      }),
      invalidatesTags: ['Session'],
      async onQueryStarted(arg, { queryFulfilled }) {
        log.debug({ message_type: 'Session', message: 'impersonate: pending' });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Session',
            message: 'impersonate: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'impersonate: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
      transformResponse: (response) => {
        log.debug({
          message_type: 'Session',
          message: 'impersonate: transformResponse',
          data: response,
        });
        const mapped = { ...response };
        if (response.session_id) mapped.sessionId = response.session_id;
        if (response.tenant_id) mapped.tenantId = response.tenant_id;
        if (response.client_id) mapped.clientId = response.client_id;
        if (response.user_id) mapped.userId = response.user_id;
        return mapped;
      },
    }),
  }),
});

export const {
  useLazyGetSessionQuery,
  useLazySearchTransactionsQuery,
  useLazyGetReceiptQuery,
  useRequestImpersonationSessionMutation,
} = sessionApi;
